// The game: six rows, then the phoenix. Every move is checked locally with @wbwl/rules for an
// instant answer, shown at once, and saved to the API; the server's answer replaces the local one
// (and a rejection rolls it back).
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router'
import type { EditionInfo, Play } from '@wbwl/contract'
import { scoreWord, skipPhoenix, wordListFor } from '@wbwl/rules'
import { targetOf, VsBar } from '../components/challenge-ui.tsx'
import { Ashes, Board, Keyboard, Preview, ScoreHead, Tile } from '../components/game-ui.tsx'
import { EditionPanel, ScorecardPanel } from '../components/GameSides.tsx'
import { HowTo } from '../components/HowTo.tsx'
import { Icon } from '../components/Icon.tsx'
import { canCatch, catchAt, catchDone } from '../components/scorch.tsx'
import { ErrorNote, Loading, NotFound, useToast } from '../components/shell.tsx'
import { ApiFailure, api } from '../lib/api.ts'
import { useDayOver, useEdition, useEditionChallenges, usePlay, useStats } from '../lib/data.ts'
import { applyMove, applyPhoenix, burnCols, burnedOnRow, localPlay, rejectionText, shortDate, storage, toEdition, toState } from '../lib/game.ts'
import { useFit } from '../lib/layout.ts'
import { PHOENIX_BLOOM_MS, playPhoenix, playSubmit, playTick, preloadPhoenix, primeSound, setSoundOn, soundOn } from '../lib/sound.ts'
import { invalidate, setResource } from '../lib/store.ts'

export default function GamePage() {
  const params = useParams()
  const n = params.number ? Number(params.number) : 'today'
  if (n !== 'today' && (!Number.isInteger(n) || n < 0)) return <NotFound />
  return <GameLoader n={n} />
}

function GameLoader({ n }: { n: number | 'today' }) {
  const ed = useEdition(n)
  const info = ed.data
  const pq = usePlay(info?.number ?? null)
  // Only an edition that was already finished when you opened it jumps straight to results; one
  // you finish here stays put long enough to see the last word land.
  const doneOnOpen = useRef<{ n: number; done: boolean } | null>(null)
  if (pq.data && doneOnOpen.current?.n !== pq.data.edition) doneOnOpen.current = { n: pq.data.edition, done: pq.data.phase === 'done' }

  if (ed.error) return <div className="page"><ErrorNote error={ed.error} retry={() => ed.reload()} /></div>
  if (!info || !pq.data) return pq.error ? <div className="page"><ErrorNote error={pq.error} retry={() => pq.reload()} /></div> : <div className="page"><Loading /></div>
  if (doneOnOpen.current?.done) return <Navigate to={`/results/${info.number}`} replace />
  return <Game key={info.number} info={info} play={pq.data} />
}

/** The challenge bar's height and gap, which the board gives up when it's showing. */
const VS_BAR_PX = 46

/** How long a word's toast (its score and what burned) stays up. */
const WORD_TOAST_MS = 3800
/** When the toasts that follow a word's come up: out of vowels, no phoenix. */
const FOLLOW_UP_MS = WORD_TOAST_MS + 100

function Game({ info, play }: { info: EditionInfo; play: Play }) {
  const toast = useToast()
  const navigate = useNavigate()
  // In a challenge someone has finished: the score to beat, under your own (on desktop, beside it).
  const challengeList = useEditionChallenges(info.number)
  const challenges = challengeList.data?.challenges
  const target = play.phase === 'done' || !challenges ? null : targetOf(challenges)
  const fit = useFit(target ? VS_BAR_PX : 0)
  const edition = useMemo(() => toEdition(info), [info])
  const words = useMemo(() => wordListFor(edition), [edition])
  const key = `play:${info.number}`
  const [typing, setTypingState] = useState('')
  const [phx, setPhxState] = useState('')
  // Refs mirror the typed words so a fast Enter right after a letter sees that letter.
  const typingRef = useRef('')
  const phxRef = useRef('')
  const setTyping = (v: string) => { typingRef.current = v; setTypingState(v) }
  const setPhx = (v: string) => { phxRef.current = v; setPhxState(v) }
  const busyRef = useRef(false)
  const [shake, setShake] = useState<number | null>(null)
  const [flipRow, setFlipRow] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  // The phoenix rising after Raise: the round stays up for its animation, and the score jumps on the bloom.
  const [rising, setRising] = useState(false)
  const [risen, setRisen] = useState(false)
  const [howto, setHowto] = useState(false)
  const [sound, setSound] = useState(soundOn)
  // A first game: How to play opens by itself, then the tips. Your account has the last word, so a
  // player new only to this device (a new phone, the home-screen app) skips both.
  const [coach, setCoach] = useState(() => play.rows.length === 0 && !storage.get('lof:coached'))
  const [introduced, setIntroduced] = useState(false)
  const stats = useStats(coach)
  const playedBefore = !!stats.data && stats.data.history.length > 0
  const intro = coach && !playedBefore && !introduced && !!(stats.data || stats.error)
  useEffect(() => {
    if (coach && playedBefore) { storage.set('lof:coached', '1'); setCoach(false) }
  }, [coach, playedBefore])

  const invalid = play.phase === 'play' && typing.length === 5 && !words.has(typing)
  const phoenixInvalid = play.phase === 'phoenix' && phx.length === 5 && !words.has(phx)

  const bump = (row: number) => { setShake(row); setTimeout(() => setShake(null), 450) }

  // Midnight Pacific passed mid-game: a game started on its day ends with it, keeping its score.
  // Ask the server (which ends it), then say so and go to the results.
  const dayOver = useDayOver(info.nextAt)
  const dayEndedNote = () => toast(`No. ${info.number}’s day is over, so your game ended with it. Your ${play.scores.total.toLocaleString('en-US')} points count.`, 3600)
  useEffect(() => {
    if (!dayOver || !play.ranked || play.phase === 'done' || busyRef.current) return
    let live = true
    api.get<Play>(`/plays/${info.number}`).then((p) => {
      // A move under way brings the same news with its answer (see save).
      if (!live || p.phase !== 'done' || busyRef.current) return
      setResource(key, p)
      dayEndedNote()
      finish(p, 2600)
    }, () => {})
    return () => { live = false }
  }, [dayOver, play.ranked, play.phase])

  const finish = useCallback((p: Play, after?: number) => {
    invalidate('me', 'stats', 'board:', `edition:${info.number}`, 'edition:today', `thread:${info.number}`, 'edchallenges:', 'mychallenges', 'challenge:', 'chat:', 'rivals', 'rivalry:')
    setTimeout(() => navigate(`/results/${p.edition}`, { state: { finished: true } }), after ?? (p.phoenixResult === 'no_word' ? 2200 : 900))
  }, [info.number, navigate])

  async function save(path: string, body: unknown, optimistic: Play, onFail: () => void): Promise<Play | null> {
    setResource(key, optimistic)
    busyRef.current = true
    setBusy(true)
    try {
      const server = await api.post<Play>(path, body)
      setResource(key, server)
      // A word sent after midnight finds the game already ended with its day.
      if (server.phoenixResult === 'day_over' && play.phoenixResult !== 'day_over') setTimeout(dayEndedNote, 200)
      return server
    } catch (e) {
      setResource(key, play)
      onFail()
      toast(e instanceof ApiFailure ? e.body.message || reasonFromApi(e) : 'Couldn’t save that. Check your connection and try again.', 2600)
      return null
    } finally {
      busyRef.current = false
      setBusy(false)
    }
  }

  /** A toast, after the word's own, the moment you pass the score you're chasing. */
  function passed(total: number, after: number) {
    if (target && play.scores.total <= target.total && total > target.total) {
      setTimeout(() => toast(<><Icon name="crown" />You passed <b>{target.name}</b></>, 2200), after)
    }
  }

  async function submitRow() {
    const word = typingRef.current
    const state = toState(play)
    if (word.length < 5) return toast('Five letters, please')
    const r = applyMove(edition, state, word, words)
    if (!r.ok) { toast(rejectionText(r, word)); return bump(state.rows.length) }
    const row = state.rows.length
    const optimistic = localPlay(info, play, r.state)
    setTyping('')
    // The row flips in; its burn tiles then light their fuses and catch (scorch.tsx), and the burn
    // sounds as the first one catches. The next word's row takes over the flip if it comes sooner.
    const bc = burnCols(info, row)
    const fire = bc.length > 0 && canCatch(fit.tile)
    setFlipRow(row); setTimeout(() => setFlipRow((f) => (f === row ? null : f)), fire ? catchDone(bc[bc.length - 1]) : 800)
    playSubmit(r.burned.length > 0, fire ? catchAt(bc[0]) : 0)
    const up = optimistic.standing.index > play.standing.index
    passed(optimistic.scores.total, WORD_TOAST_MS - 100)
    toast(<><span className="sc">+{r.score.total}</span><span>{r.burned.length ? `${r.burned.join(', ')} burned` : word}{up ? <> · You made <b>{optimistic.standing.name}</b></> : null}</span></>, WORD_TOAST_MS)
    if (r.state.rowsEnd === 'out_of_vowels') setTimeout(() => toast('Out of vowels — straight to the phoenix', 2400), FOLLOW_UP_MS)
    if (r.state.phoenixResult === 'no_word') setTimeout(() => toast('Nothing in your ashes spells a word — no phoenix today', 2400), FOLLOW_UP_MS)
    const server = await save(`/plays/${info.number}/moves`, { word }, optimistic, () => setTyping(word))
    if (server?.phase === 'done') finish(server)
  }

  async function raisePhoenix() {
    const word = phxRef.current
    if (word.length < 5) return toast('Five ashes, please')
    const r = applyPhoenix(edition, toState(play), word, words)
    if (!r.ok) return toast(rejectionText(r, word))
    const optimistic = localPlay(info, play, r.state)
    const started = performance.now()
    playPhoenix()
    setRising(true)
    const bloom = setTimeout(() => {
      setRisen(true)
      toast(<><span className="sc">+{r.score.total}</span><span>{word} rises</span></>, 2000)
      passed(optimistic.scores.total, 1100)
    }, PHOENIX_BLOOM_MS)
    const server = await save(`/plays/${info.number}/phoenix`, { word }, optimistic, () => {
      clearTimeout(bloom)
      setRising(false)
      setRisen(false)
      setPhx(word)
    })
    if (server) finish(server, Math.max(0, riseMs() - (performance.now() - started)))
  }

  async function skip() {
    const r = skipPhoenix(toState(play))
    if (!r.ok) return
    const server = await save(`/plays/${info.number}/phoenix`, { skip: true }, localPlay(info, play, r.state), () => {})
    if (server) finish(server)
  }

  // Letters keep working while a word saves; only Enter waits for the server.
  const onKey = (k: string) => {
    primeSound()
    if (play.phase === 'phoenix') preloadPhoenix()
    if (coach) return
    if (k === 'ENTER' && busyRef.current) return
    if (play.phase === 'play') {
      const t = typingRef.current
      if (k === 'DEL') return setTyping(t.slice(0, -1))
      if (k === 'ENTER') return void submitRow()
      const at = burnedOnRow(info, play.rows)
      if (k in at) return toast(<><Icon name="flame" style={{ color: '#FF8A5C' }} />{k} burned on row {at[k]} — it’s in your ashes</>)
      if (t.length < 5) { setTyping(t + k); playTick() }
    } else if (play.phase === 'phoenix') {
      const t = phxRef.current
      if (k === 'DEL') return setPhx(t.slice(0, -1))
      if (k === 'ENTER') return void raisePhoenix()
      if (!play.ashes.includes(k)) return toast(`${k} isn’t in your ashes`)
      if (t.includes(k)) return toast(`Each ash once — ${k} is already used`)
      if (t.length < 5) { setPhx(t + k); playTick() }
    }
  }
  const onKeyRef = useRef(onKey)
  onKeyRef.current = onKey
  // One function for the keyboard's life, so a letter typed doesn't redraw every key.
  const pressKey = useCallback((k: string) => onKeyRef.current(k), [])

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const tag = (e.target as HTMLElement | null)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      if (e.key === '?') { setHowto(true); return }
      if (e.key === 'Enter') { e.preventDefault(); onKeyRef.current('ENTER') }
      else if (e.key === 'Backspace') { e.preventDefault(); onKeyRef.current('DEL') }
      else if (/^[a-z]$/i.test(e.key)) onKeyRef.current(e.key.toUpperCase())
    }
    window.addEventListener('keydown', down)
    return () => window.removeEventListener('keydown', down)
  }, [])

  const kbVars = { '--kw': `${fit.keyW}px`, '--kh': `${fit.keyH}px`, '--kgap': `${fit.keyGap}px`, '--kwide': `${fit.keyWide}px` } as CSSProperties
  const total = play.scores.total
  const archive = !play.ranked

  return (
    <div className={`page game-page${fit.wide ? ' wide' : ''}`}>
      {fit.wide && <EditionPanel info={info} total={total} target={target} />}
      <main className="game-main">
        {/* On desktop the header has sound and how-to-play, and back sits with the score below. */}
        {!fit.desktop && <div className="topbar game-top">
          <Link className="ibtn" to="/" aria-label="Back to today"><Icon name="back" /></Link>
          <div className="title"><b>No. {info.number}</b><small>{shortDate(info.date)}{archive ? ' · ARCHIVE' : ''}</small></div>
          <button type="button" className="ibtn" aria-label={sound ? 'Turn sound off' : 'Turn sound on'} onClick={() => { setSoundOn(!sound); setSound(!sound); if (!sound) primeSound() }}><Icon name={sound ? 'sound' : 'muted'} /></button>
          <button type="button" className="ibtn" aria-label="How to play" onClick={() => setHowto(true)}><Icon name="help" /></button>
          <Link className="ibtn" to="/you" aria-label="Your stats"><Icon name="chart" /></Link>
        </div>}

        <div className={`score-wrap${risen ? ' risen' : ''}`}>
          <ScoreHead
            total={play.phase === 'phoenix' || (rising && !risen) ? play.scores.rowsTotal : total}
            ranks={info.ranks}
            ladderWidth={fit.desktop ? 200 : 168}
            lead={fit.desktop ? <Link className="ibtn" to="/" aria-label="Back to today"><Icon name="back" /></Link> : undefined}
            label={fit.desktop ? `No. ${info.number}${archive ? ' · archive' : ''} · score` : undefined}
          />
        </div>
        {/* Without the side panels (phones and narrower desktops) the score to beat sits under the score. */}
        {target && !fit.wide && <VsBar target={target} total={play.phase === 'phoenix' || (rising && !risen) ? play.scores.rowsTotal : total} />}

        {play.phase === 'play' ? (
          <>
            <div className="board-wrap" data-coach="board">
              <Board edition={info} rows={play.rows} typing={typing} size={fit.tile} gap={fit.desktop ? 7 : 6} shakeRow={shake} invalid={invalid} flipRow={flipRow} />
            </div>
            <div className="ashes-wrap"><Ashes edition={info} ashes={play.ashes} align={{ tile: fit.tile, gap: fit.desktop ? 7 : 6 }} /></div>
            <div className="dock" style={kbVars}>
              <div className="preview-wrap"><Preview edition={info} word={typing} row={play.rows.length} ashes={play.ashes} invalid={invalid} /></div>
              <div data-coach="keys"><Keyboard edition={info} ashes={play.ashes} onKey={pressKey} /></div>
            </div>
          </>
        ) : play.phase === 'phoenix' || rising ? (
          <PhoenixRound info={info} play={play} phx={phx} invalid={phoenixInvalid} busy={busy || rising} rising={rising} onKey={onKey} onSkip={skip} tile={fit.tile} />
        ) : (
          <div className="finished">
            <div className="board-wrap"><Board edition={info} rows={play.rows} size={fit.tile} gap={fit.desktop ? 7 : 6} /></div>
            <div className="ashes-wrap"><Ashes edition={info} ashes={play.ashes} used={play.phoenix ?? undefined} align={{ tile: fit.tile, gap: fit.desktop ? 7 : 6 }} /></div>
            <Link className="btn ink block finished-cta" to={`/results/${info.number}`} state={{ finished: true }}>See your results</Link>
          </div>
        )}
      </main>
      {fit.wide && <ScorecardPanel info={info} play={play} typing={typing} valid={!invalid} target={target} />}
      <HowTo open={howto || intro} onClose={() => { setHowto(false); setIntroduced(true) }} />
      {/* Wait for the challenge list, whose bar moves the board, then measure again if the layout changes. */}
      {coach && introduced && !!(challengeList.data || challengeList.error) && (
        <CoachMarks layout={`${fit.tile}:${fit.desktop}:${fit.wide}:${target !== null}`} onDone={() => { storage.set('lof:coached', '1'); setCoach(false) }} />
      )}
    </div>
  )
}

function reasonFromApi(e: ApiFailure): string {
  const b = e.body
  switch (b.error) {
    case 'not_a_word': return 'That isn’t in the word list'
    case 'burned_letter': return `${(b.letters ?? []).join(', ')} already burned`
    case 'already_played': return `You played that on row ${(b.row ?? 0) + 1}`
    case 'rate_limited': return 'Slow down a little and try again'
    case 'future_edition': return 'That edition isn’t out yet'
    default: return 'That didn’t go through. Try again.'
  }
}

/** How long the phoenix rises before the results open; app.css lifts the round away from 1.8 s. */
const RISE_MS = 2300
const riseMs = () => (matchMedia('(prefers-reduced-motion: reduce)').matches ? 900 : RISE_MS)

/** Embers for the rise: where each starts across the ashes, how far it drifts, when and how big. */
const EMBERS = Array.from({ length: 22 }, (_, i) => {
  const r = (n: number) => ((Math.sin(i * 12.9898 + n * 78.233) * 43758.5453) % 1 + 1) % 1
  return { x: Math.round(r(1) * 260 - 130), y: Math.round(r(2) * 100), dx: Math.round(r(3) * 70 - 35), d: Math.round(r(4) * 420), s: 4 + Math.round(r(5) * 6) }
})

function PhoenixRound({ info, play, phx, invalid, busy, rising, onKey, onSkip, tile }: { info: EditionInfo; play: Play; phx: string; invalid: boolean; busy: boolean; rising: boolean; onKey(k: string): void; onSkip(): void; tile: number }) {
  const s = phx.length === 5 && !invalid ? scoreWord(phx, info) : null
  const after = s ? play.scores.rowsTotal + s.total : null
  const rank = after !== null ? info.ranks.reduce((name, r) => (after >= r.at ? r.name : name), info.ranks[0].name) : null
  const slot = Math.min(64, Math.round(tile * 1.15))
  const root = useRef<HTMLDivElement>(null)
  const slots = useRef<HTMLDivElement>(null)
  const keys = useRef<HTMLDivElement>(null)
  // The embers climb from the ash tiles to the phoenix's slots, so measure both as the rise starts.
  const [path, setPath] = useState<{ from: number; rise: number } | null>(null)
  useEffect(() => { preloadPhoenix() }, [])
  useLayoutEffect(() => {
    if (!rising || !root.current || !slots.current || !keys.current) return
    const top = root.current.getBoundingClientRect().top
    const k = keys.current.getBoundingClientRect()
    const sl = slots.current.getBoundingClientRect()
    setPath({ from: k.top - top, rise: k.top - sl.top })
  }, [rising])
  const vars = path ? ({ '--from': `${path.from}px`, '--rise': `${path.rise}px` } as CSSProperties) : undefined
  // Skipping ends the game for good, so it takes a second tap.
  const [skipping, setSkipping] = useState(false)
  return (
    <div className={`phoenix${rising ? ' rising' : ''}`} ref={root} style={vars}>
      <div className="phx-glow" />
      <div className="phx-intro">
        <Board edition={info} rows={play.rows} size={20} gap={3} scores={false} dimPlayed />
        <div>
          <div className="eyebrow burn-ink">{play.rows.length} words down · {play.scores.rowsTotal}</div>
          <h2 className="h-serif">Rise from<br />the <i>ashes</i></h2>
          <p className="muted small">One more word, using only letters you burned — each once. Burned gold still counts.</p>
        </div>
      </div>
      <div className="phx-slots" ref={slots}>
        {rising && <span className="phx-burst" aria-hidden="true"><Icon name="flame" /></span>}
        {[0, 1, 2, 3, 4].map((i) => {
          const l = phx[i]
          return <Tile key={`${i}${l ?? ''}`} l={l} k={l ? `typed pop${info.gold[l] ? ' gold' : ''}` : 'cur'} size={slot} gold={info.gold} rv={info.rulesVersion} style={{ '--i': i } as CSSProperties} />
        })}
      </div>
      <div className="phx-preview">
        <div className="preview">
          {s ? <><span className="math"><b>{s.pts}</b> pts × <em>{s.mult}</em></span><span className="eq" style={{ marginLeft: 'auto' }}>{s.total}</span>{rank && <span className="chip ink"><Icon name="crown" />{rank}</span>}</> : <span className="math">{invalid ? `${phx} isn’t in the word list` : 'Phoenix · letters × multiplier'}</span>}
        </div>
        {after !== null && <div className="faint mono center small" style={{ marginTop: 8 }}>{play.scores.rowsTotal} + {s!.total} = {after}</div>}
      </div>
      <div className="phx-keys">
        <div className="eyebrow burn-ink center">Your ashes · tap to spell</div>
        <div className="ashkeys" ref={keys}>
          {play.ashes.map((l) => {
            const spent = phx.includes(l)
            return (
              <button key={l} type="button" tabIndex={-1} className="ashkey" disabled={spent || busy} onMouseDown={(e) => e.preventDefault()} onClick={() => onKey(l)} aria-label={`Ash ${l}`} style={spent ? ({ '--i': phx.indexOf(l) } as CSSProperties) : undefined}>
                <Tile l={l} k={`ash${spent ? ' spent' : ''}${info.gold[l] ? ' gold' : ''}`} size={52} pt={false} gold={info.gold} />
              </button>
            )
          })}
          <button type="button" tabIndex={-1} className="k wide" aria-label="Delete" style={{ width: 52, height: 52, borderRadius: 10 }} onMouseDown={(e) => e.preventDefault()} onClick={() => onKey('DEL')}><Icon name="del" size={22} /></button>
        </div>
        {skipping ? (
          <div className="phx-actions confirm" role="alertdialog" aria-label="Skip the phoenix?">
            <p className="small"><b>Skip the phoenix?</b> Your game ends at {play.scores.rowsTotal} and you can’t come back to it.</p>
            <button type="button" className="btn ghost" onClick={() => setSkipping(false)} autoFocus>Keep playing</button>
            <button type="button" className="btn ink" onClick={onSkip} disabled={busy}>Skip</button>
          </div>
        ) : (
          <div className="phx-actions">
            <button type="button" className="btn ghost" onClick={() => setSkipping(true)} disabled={busy}>Skip</button>
            <button type="button" className="btn gold" onClick={() => onKey('ENTER')} disabled={busy || !s}><Icon name="feather" />Raise {s ? phx : 'phoenix'}</button>
          </div>
        )}
      </div>
      {rising && path && (
        <div className="phx-embers" aria-hidden="true">
          {EMBERS.map((e, i) => <i key={i} style={{ '--x': `${e.x}px`, '--y': `${e.y}px`, '--dx': `${e.dx}px`, '--d': `${e.d}ms`, '--s': `${e.s}px` } as CSSProperties} />)}
        </div>
      )}
    </div>
  )
}

/**
 * Two tips on a first visit, one at a time: the first burn tile, then the gold keys. Each tip
 * carries its own button (Next, then Got it), and a tap anywhere else moves on too. A tip sits
 * below what it points at when there's room, else above it. `layout` names the screen's layout
 * (tile size, desktop, challenge bar): when it changes, the tips measure again.
 */
function CoachMarks({ layout, onDone }: { layout: string; onDone(): void }) {
  const [pos, setPos] = useState<{ burn?: DOMRect; keys?: DOMRect } | null>(null)
  const [step, setStep] = useState(0)
  useEffect(() => {
    const measure = () => {
      const burn = document.querySelector('[data-coach="board"] .brow .t.burn')?.getBoundingClientRect()
      // Ring the gold keys of whichever keyboard row has the most of them.
      const rows = [...document.querySelectorAll('[data-coach="keys"] .kb-row')].map((r) => [...r.querySelectorAll('.k.gold')].map((k) => k.getBoundingClientRect()))
      const gold = rows.reduce((a, b) => (b.length > a.length ? b : a), [] as DOMRect[])
      const keys = gold.length ? new DOMRect(Math.min(...gold.map((g) => g.left)), gold[0].top, Math.max(...gold.map((g) => g.right)) - Math.min(...gold.map((g) => g.left)), gold[0].height) : undefined
      setPos({ burn, keys })
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [layout])
  if (!pos) return null
  const tips = [
    pos.burn && { at: pos.burn, radius: 14, text: <><b>Burn tile.</b> Whatever you put here burns when you press Enter — that letter is gone for the rest of today.</> },
    pos.keys && { at: pos.keys, radius: 12, text: <><b>Gold keys</b> add +1, +2 or +3 to your multiplier, each time you use one.</> },
  ].filter((t) => !!t)
  const tip = tips[Math.min(step, tips.length - 1)]
  if (!tip) return null
  const last = step >= tips.length - 1
  const next = () => (last ? onDone() : setStep(step + 1))
  const { at } = tip
  const below = at.bottom + 14 + 170 < window.innerHeight
  const place = below ? { top: at.bottom + 14 } : { bottom: window.innerHeight - at.top + 14 }
  return (
    <div className="coach-layer" onClick={next}>
      <div className="ring" style={{ left: at.left - 5, top: at.top - 5, width: at.width + 10, height: at.height + 10, borderRadius: tip.radius }} />
      <div className={`coach ${below ? 'up' : 'down'}`} role="dialog" aria-label={`Tip ${step + 1} of ${tips.length}`} style={{ left: Math.max(12, Math.min(window.innerWidth - 252, at.left - 16)), ...place }} onClick={(e) => e.stopPropagation()}>
        <div><span className="n">{step + 1}</span>{tip.text}</div>
        <div className="coach-foot">
          <span>{step + 1} of {tips.length}</span>
          <button type="button" className="btn gold sm" onClick={next} autoFocus>{last ? 'Got it' : 'Next'}</button>
        </div>
      </div>
    </div>
  )
}
